import { useState, useRef, useEffect } from 'react';
import { Bell, User, LogOut, Settings, UserCircle, Send, X } from 'lucide-react';
import { User as UserType } from '../App';
import api from '../services/api';

const BOOKING_REMINDER_RULES = [
  { key: 'hotelBookingMails', label: 'Hotel Booking mails', dueDays: 30 },
  { key: 'vouchers', label: 'Vouchers', dueDays: 45 },
  { key: 'trainBooking', label: 'Train Booking', dueDays: 65 },
  { key: 'flightBooking', label: 'Flight Booking', dueDays: 60 },
  { key: 'safariBooking', label: 'Safari Booking', dueDays: 150 }
];

interface Notification {
  id: string;
  message: string;
  createdAt: Date;
  isRead: boolean;
  author: string;
  authorId: string;
}

const getNotificationDedupKey = (notification: Pick<Notification, 'message' | 'author'>) => {
  return `${notification.author.trim().toLowerCase()}::${notification.message.trim().toLowerCase()}`;
};

interface TopBarProps {
  currentUser: UserType;
  onLogout?: () => void;
}

export function TopBar({ currentUser, onLogout }: TopBarProps) {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [newComment, setNewComment] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);
  const notificationRef = useRef<HTMLDivElement>(null);

  // Load notifications from API on mount and every 30 seconds
  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000); // Poll every 30 seconds
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const syncDueReminders = async () => {
      const now = new Date();
      const reminderKeys = Object.keys(localStorage).filter((key) => key.startsWith('tour-booking-reminders-'));

      for (const storageKey of reminderKeys) {
        const tourIdentifier = storageKey.replace('tour-booking-reminders-', '');

        try {
          const raw = localStorage.getItem(storageKey);
          if (!raw) continue;

          const reminders = JSON.parse(raw) as Record<string, any>;
          let shouldPersist = false;

          for (const rule of BOOKING_REMINDER_RULES) {
            const reminder = reminders[rule.key] || { completed: false, snoozeDays: 0 };
            if (reminder.completed) continue;

            const dueDate = reminders.__tourStartDate ? new Date(reminders.__tourStartDate) : null;
            if (!dueDate || Number.isNaN(dueDate.getTime())) continue;

            dueDate.setDate(dueDate.getDate() - rule.dueDays);
            if (dueDate > now) continue;

            if (reminder.snoozedUntil && new Date(reminder.snoozedUntil) > now) continue;

            if (reminder.notifiedAt && !reminder.snoozedUntil) continue;

            await api.notification.create({
              message: `${rule.label} is due for tour ${tourIdentifier}. Please complete it or snooze it from Tickets & Bookings.`,
              author: currentUser.name,
              authorId: currentUser.id || ''
            });

            reminders[rule.key] = { ...reminder, notifiedAt: new Date().toISOString(), snoozedUntil: undefined };
            shouldPersist = true;
          }

          if (shouldPersist) {
            localStorage.setItem(storageKey, JSON.stringify(reminders));
          }
        } catch (error) {
          console.error('Failed to sync booking reminders:', error);
        }
      }
    };

    syncDueReminders();
    const interval = setInterval(syncDueReminders, 30000);
    return () => clearInterval(interval);
  }, [currentUser.id, currentUser.name]);

  const loadNotifications = async () => {
    try {
      const response = await api.notification.getAll();
      if (response.success && response.data) {
        const deduped = new Map<string, Notification>();

        response.data
          .map((notification: any) => ({
            ...notification,
            createdAt: new Date(notification.createdAt)
          }))
          .sort((a: Notification, b: Notification) => b.createdAt.getTime() - a.createdAt.getTime())
          .forEach((notification: Notification) => {
            const key = getNotificationDedupKey(notification);
            if (!deduped.has(key)) {
              deduped.set(key, notification);
            }
          });

        setNotifications(Array.from(deduped.values()));
      }
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowProfileMenu(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      const response = await api.notification.create({
        message: newComment.trim(),
        author: currentUser.name,
        authorId: currentUser.id || ''
      });
      
      console.log('✅ Notification created:', response);
      
      if (response.success) {
        setNewComment('');
        await loadNotifications(); // Reload to show new notification
      } else {
        throw new Error(response.error || 'Failed to create notification');
      }
    } catch (error: any) {
      console.error('❌ Failed to add notification:', error);
      alert(`Failed to add notification: ${error.message || 'Unknown error'}`);
    }
  };

  const handleDeleteNotification = async (id: string) => {
    try {
      await api.notification.delete(id);
      await loadNotifications();
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.notification.markAllAsRead();
      await loadNotifications();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const clearAllNotifications = async () => {
    try {
      await api.notification.clearAll();
      await loadNotifications();
    } catch (error) {
      console.error('Failed to clear notifications:', error);
    }
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <header className="sticky top-0 z-10 h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 shadow-sm">
      <div>
        <p className="text-gray-500 text-sm">Welcome back,</p>
        <p className="font-semibold text-gray-900">{currentUser.name}</p>
      </div>
      
      <div className="flex items-center gap-4">
        {/* Notification Bell */}
        <div className="relative" ref={notificationRef}>
          <button 
            onClick={() => {
              setShowNotifications(!showNotifications);
              setShowProfileMenu(false);
            }}
            className="relative p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <Bell className="w-5 h-5 text-gray-600" />
            {unreadCount > 0 && (
              <span className="absolute top-0 right-0 w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center font-medium">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {/* Notifications Dropdown */}
          {showNotifications && (
            <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-[500px] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between bg-gray-50">
                <h3 className="font-semibold text-gray-900">Notifications & Comments</h3>
                {notifications.length > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    Mark all as read
                  </button>
                )}
              </div>

              {/* Add Comment Input */}
              <div className="p-3 border-b border-gray-200">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                    placeholder="Add a comment or note..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  />
                  <button
                    onClick={handleAddComment}
                    disabled={!newComment.trim()}
                    className="px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Notifications List */}
              <div className="overflow-y-auto flex-1 max-h-80">
                {notifications.length === 0 ? (
                  <div className="px-4 py-8 text-center text-gray-500">
                    <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">No notifications yet</p>
                    <p className="text-xs mt-1">Add a comment above to get started</p>
                  </div>
                ) : (
                  notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                        !notification.isRead ? 'bg-purple-50' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <p className="text-sm text-gray-900">{notification.message}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-purple-600 font-medium">{notification.author}</span>
                            <span className="text-xs text-gray-400">•</span>
                            <span className="text-xs text-gray-400">
                              {notification.createdAt.toLocaleDateString('en-IN', {
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteNotification(notification.id)}
                          className="p-1 hover:bg-gray-200 rounded transition-colors"
                        >
                          <X className="w-4 h-4 text-gray-400 hover:text-red-500" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {notifications.length > 0 && (
                <div className="px-4 py-2 border-t border-gray-200 bg-gray-50">
                  <button
                    onClick={clearAllNotifications}
                    className="text-xs text-red-600 hover:text-red-800"
                  >
                    Clear all notifications
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="relative" ref={menuRef}>
          <button 
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            className="flex items-center gap-3 hover:bg-gray-50 rounded-lg p-2 transition-colors"
          >
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <div className="text-left">
              <p className="text-sm font-medium">{currentUser.name}</p>
              <p className="text-xs text-gray-500 capitalize">{currentUser.role}</p>
            </div>
          </button>

          {/* Profile Dropdown Menu */}
          {showProfileMenu && (
            <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
              {/* User Info */}
              <div className="px-4 py-3 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{currentUser.name}</p>
                    <p className="text-xs text-gray-500">{currentUser.email || 'No email'}</p>
                    <p className="text-xs text-gray-500 capitalize mt-1">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                        currentUser.isAdmin 
                          ? 'bg-purple-100 text-purple-700' 
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {currentUser.isAdmin ? 'Admin' : 'Manager'}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Logout */}
              <div className="border-t border-gray-200 mt-2 pt-1">
                <button 
                  onClick={handleLogout}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 font-medium"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
